'use client';
import { useAddOrUpdateVolunteering } from '@/actions/feed.actions';
import PostImageSelector from '@components/PostImageSelector/PostImageSelector';
import PostVideoSelector from '@components/PostVideoSelector/PostVideoSelector';
import { yupResolver } from '@hookform/resolvers/yup';
import Dialog from '@ui/Dialog';
import { authorizedServerFetch } from 'actions/server.actions';
import { PublishToPublicPage } from 'components/PublishToPublicPage';
import { cx } from 'cva';
import { get, isEmpty, pick, size, sortBy } from 'lodash-es';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IoMdVideocam } from 'react-icons/io';
import { MdPhoto } from 'react-icons/md';
import StyledContainedButton from '../Styled/StyledContainedButton';
import VolunteeringForm from './VolunteeringForm';
import { defaultFormValues, schema } from './volunteeringOpportunityFormSchema';
import { IconButton } from '@mui/material';
import ActivitySuccessDialog from '../ActivitySuccessDialog';
import useUserProfile from '@/hooks/useUserProfile';
import { TargetViewers } from '@/constants/profileType.constant';
import { useBreakpointHooks } from '@/utils/break-points.provider';

function CreateVolunteeringDialog(props) {
  const { open, post = null, onClose } = props;

  const profile = useUserProfile();

  const [submitted, setSubmitted] = React.useState(false);
  const [skills, setSkills] = React.useState([]);
  const [causes, setCauses] = React.useState([]);
  const [step, setStep] = React.useState('form');
  const [rewardEarned, setRewardEarned] = React.useState(0);
  const [createdPost, setCreatedPost] = React.useState(null);
  const [selectedImages, setSelectedImages] = React.useState(null);
  const [showNoImagesError, setShowNoImagesError] = React.useState(false);
  const [selectedVideos, setSelectedVideos] = React.useState(null);
  const [showNoVideosError, setShowNoVideosError] = React.useState(false);

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const methods = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues(),
    resolver: yupResolver(schema),
  });
  const {
    getValues,
    trigger,
    formState: { isValid },
  } = methods;

  const isEditMode = !!post;

  const {
    mutateAsync: addOrUpdateOpportunity,
    isSuccess,
    isError,
  } = useAddOrUpdateVolunteering();

  useEffect(() => {
    if (submitted) {
      if (isSuccess) {
        setSubmitted(false);
      } else if (isError) {
        setSubmitted(false);
      }
    }
  }, [isSuccess, isError, submitted]);

  useEffect(() => {
    const fetchSkillsAndCauses = async () => {
      const [skillsData, causesData] = await Promise.all([
        authorizedServerFetch('/skills'),
        authorizedServerFetch('/causes'),
      ]);

      setSkills(sortBy(skillsData.data, 'skill_title'));
      setCauses(sortBy(causesData.data, 'cause_title'));
    };

    if (open) {
      fetchSkillsAndCauses();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (post) {
        methods.reset(
          defaultFormValues({
            ...pick(post, [
              'postAs',
              'targetViewers',
              'targetOrgs',
              'isFeatured',
            ]),
            ...(post.sourcePostData?.volunteeringOpportunity ||
              post.sourcePostData?.volunteerOpportunity),
          }),
        );
      } else {
        methods.reset(defaultFormValues());
      }
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    methods.reset(defaultFormValues());
    setSubmitted(false);
    setStep('form');
    setSelectedImages(null);
    setShowNoImagesError(false);
    setSelectedVideos(null);
    setShowNoVideosError(false);
  };

  const onFormSubmit = async () => {
    if (step === 'form') {
      if (isValid) {
        if (size(selectedImages) > 0 && size(selectedVideos) > 0) {
          setStep('publish');
          setShowNoImagesError(false);
          setShowNoVideosError(false);
        } else if (size(selectedImages) > 0) {
          setStep('publish');
          setShowNoImagesError(false);
          setShowNoVideosError(false);
        } else if (size(selectedVideos) > 0) {
          setStep('publish');
          setShowNoImagesError(false);
          setShowNoVideosError(false);
        } else {
          setShowNoImagesError(true);
          setShowNoVideosError(true);
        }
      } else {
        trigger();
      }
    } else {
      setSubmitted(true);
      const formValues = getValues();
      const data = await addOrUpdateOpportunity({
        formValues,
        post,
        images: selectedImages,
        videos: selectedVideos,
      });
      setRewardEarned(data?.reward);
      setCreatedPost(data?.post);
      setStep('success');
    }
  };

  const handleAddPictures = () => {
    setStep('pictures');
  };

  const handleAddVideos = () => {
    setStep('videos');
  };

  const updatePostImages = (images) => {
    setSelectedImages(images);
    if (!isEmpty(images)) {
      setShowNoImagesError(false);
      setShowNoVideosError(false);
      setSelectedVideos(null);
    }
  };

  const updatePostVideos = (videos) => {
    setSelectedVideos(videos);
    if (!isEmpty(videos)) {
      setShowNoImagesError(false);
      setShowNoVideosError(false);
      setSelectedImages(null);
    }
  };

  const handleImageDiscard = () => {
    setStep('form');
    setSelectedImages(null);
  };

  const handleVideoDiscard = () => {
    setStep('form');
    setSelectedVideos(null);
  };

  const getTitle = () => {
    switch (step) {
      case 'pictures':
        return 'Add Images';
      case 'videos':
        return 'Add Videos';
      case 'success':
        return '';
      default:
        return isEditMode
          ? 'Edit Volunteering Opportunity'
          : 'Create Volunteering Opportunity';
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      maxWidth="lg"
      title={getTitle()}>
      <div className="flex w-full flex-col gap-4 overflow-hidden p-0 lg:p-0">
        <div
          className={`flex flex-col-reverse gap-4 overflow-y-auto overflow-x-hidden lg:flex-row ${
            isLessThanSmallScreen ? '' : 'flex-1'
          }`}>
          <FormProvider {...methods}>
            <div className={cx(step !== 'form' && 'hidden', 'w-full pr-[5px]')}>
              <VolunteeringForm
                skills={skills}
                causes={causes}
                images={
                  selectedImages ||
                  get(
                    post,
                    'sourcePostData.volunteeringOpportunity.imageUrls',
                    [],
                  )
                }
                videos={
                  selectedVideos ||
                  get(
                    post,
                    'sourcePostData.volunteeringOpportunity.attachmentVideos',
                    [],
                  ) ||
                  get(
                    post,
                    'sourcePostData.volunteeringOpportunity.videoUrls',
                    [],
                  )
                }
                isEditMode={isEditMode}
              />
            </div>
            {step === 'publish' && (
              <div className="flex-1">
                <PublishToPublicPage />
              </div>
            )}
          </FormProvider>

          {step === 'pictures' && (
            <PostImageSelector
              onChange={updatePostImages}
              value={selectedImages}
            />
          )}

          {step === 'videos' && (
            <PostVideoSelector
              onChange={updatePostVideos}
              value={selectedVideos}
            />
          )}

          {step === 'success' && (
            <ActivitySuccessDialog
              // nonProfit={profile?.nonProfitCompany}
              // company={profile?.company}
              user={profile}
              onClose={handleClose}
              reward={rewardEarned}
              post={createdPost}
              isPostPublic={createdPost?.targetViewers === TargetViewers.All}
              isPost
            />
          )}
        </div>

        {/* <Divider /> */}

        <div
          className={`flex-rows flex justify-end gap-4 pb-2 ${
            isLessThanSmallScreen ? 'mb-32' : ''
          }`}>
          {step === 'form' && (
            <div className="flex w-full items-center justify-between">
              <div className="flex gap-2">
                <IconButton onClick={handleAddPictures}>
                  <MdPhoto
                    className={`${
                      showNoImagesError ? 'animate-pulse text-error' : ''
                    }`}
                  />
                </IconButton>
                <IconButton onClick={handleAddVideos}>
                  <IoMdVideocam
                    className={`${
                      showNoVideosError ? 'animate-pulse text-error' : ''
                    }`}
                  />
                </IconButton>
              </div>
              <StyledContainedButton
                type="button"
                loading={submitted}
                onClick={onFormSubmit}>
                {isEditMode ? 'Update' : 'Next'}
              </StyledContainedButton>
            </div>
          )}
          {step === 'pictures' && (
            <div className="flex gap-2">
              <StyledContainedButton
                type="button"
                invertColors
                onClick={handleImageDiscard}>
                Back
              </StyledContainedButton>
              <StyledContainedButton
                type="button"
                disabled={isEmpty(selectedImages)}
                onClick={() => setStep('form')}>
                Done
              </StyledContainedButton>
            </div>
          )}
          {step === 'videos' && (
            <div className="flex gap-2">
              <StyledContainedButton
                type="button"
                invertColors
                onClick={handleVideoDiscard}>
                Back
              </StyledContainedButton>
              <StyledContainedButton
                type="button"
                disabled={isEmpty(selectedVideos)}
                onClick={() => setStep('form')}>
                Done
              </StyledContainedButton>
            </div>
          )}
          {step === 'publish' && (
            <StyledContainedButton
              type="button"
              loading={submitted}
              onClick={onFormSubmit}>
              Publish
            </StyledContainedButton>
          )}
          {/* {step === 'success' && (
            <StyledContainedButton type="button" onClick={() => handleClose()}>
              Close
            </StyledContainedButton>
          )} */}
        </div>
      </div>
    </Dialog>
  );
}

export default CreateVolunteeringDialog;
